/** Primary fulfillment chain (admin may only move forward, not backward). */
export const ORDER_STATUS_FORWARD_CHAIN = ['CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP', 'ON_THE_WAY', 'DELIVERED'];

/** Primary fulfillment chain for store pickup / takeaway orders (skips ON_THE_WAY). */
export const PICKUP_STATUS_FORWARD_CHAIN = ['CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP', 'DELIVERED'];

/** Customer-style vertical tracker steps for delivery. */
export const ORDER_TRACKING_FLOW = ['CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP', 'ON_THE_WAY', 'DELIVERED'];

/** Customer-style vertical tracker steps for pickup. */
export const PICKUP_TRACKING_FLOW = ['CONFIRMED', 'PREPARING', 'READY_FOR_PICKUP', 'DELIVERED'];

const STATUS_LABELS = {
  PENDING: 'Pending confirmation',
  CONFIRMED: 'Order confirmed',
  PREPARING: 'Preparing',
  READY_FOR_PICKUP: 'Ready for pickup',
  ON_THE_WAY: 'On the way',
  DELIVERED: 'Delivered',
  REFUNDED: 'Refunded',
  CANCELLED: 'Cancelled',
};

/**
 * @param {string | undefined} status
 * @returns {string}
 */
export function humanOrderStatus(status) {
  if (!status) return '—';
  return STATUS_LABELS[status] || String(status).replace(/_/g, ' ');
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuidFromHex32(hex) {
  if (!/^[0-9a-f]{32}$/i.test(hex)) return '';
  const h = hex.toLowerCase();
  const id = `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`;
  return UUID_RE.test(id) ? id : '';
}

function uuidFromByteArray(bytes) {
  if (!Array.isArray(bytes) || bytes.length !== 16) return '';
  const hex = bytes.map((b) => Number(b).toString(16).padStart(2, '0')).join('');
  return uuidFromHex32(hex);
}

function uuidFromBase64(b64) {
  if (typeof b64 !== 'string' || !b64) return '';
  try {
    const bin = atob(b64);
    if (bin.length !== 16) return '';
    const bytes = [];
    for (let i = 0; i < 16; i += 1) bytes.push(bin.charCodeAt(i));
    return uuidFromByteArray(bytes);
  } catch {
    return '';
  }
}

/** Canonical order UUID string from API / realtime (string, BSON buffer, or Buffer POJO). */
export function normalizeOrderId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    const s = value.trim();
    if (UUID_RE.test(s)) return s;
    return uuidFromHex32(s.replace(/-/g, ''));
  }
  if (Array.isArray(value) && value.length === 16) {
    return uuidFromByteArray(value);
  }
  if (typeof value === 'object') {
    if (typeof value.$uuid === 'string') {
      return normalizeOrderId(value.$uuid);
    }
    const b64 = value.$binary?.base64 ?? value.base64;
    if (typeof b64 === 'string') {
      const fromB64 = uuidFromBase64(b64);
      if (fromB64) return fromB64;
    }
    const buf = value.buffer ?? value;
    if (Array.isArray(buf?.data) && buf.data.length === 16) {
      return uuidFromByteArray(buf.data);
    }
    if (value.type === 'Buffer' && Array.isArray(value.data) && value.data.length === 16) {
      return uuidFromByteArray(value.data);
    }
  }
  return '';
}

/** Extract business orderId from a realtime/API order payload (never Mongo _id). */
export function pickRealtimeOrderId(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return '';
  const candidates = [orderLike.orderId, orderLike.order?.orderId, orderLike.id];
  for (const c of candidates) {
    const id = normalizeOrderId(c);
    if (id) return id;
  }
  return '';
}

/** Business orderId used by GET/PATCH /v1/admin/orders/:orderId (not Mongo _id). */
export function orderRowId(row) {
  return normalizeOrderId(row?.orderId) || '';
}

/**
 * Customer-facing order number (ORD-…) or fallback to UUID.
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {string}
 */
export function orderDisplayNumber(row) {
  return (row?.orderNumber ?? row?.orderNo ?? row?.number ?? orderRowId(row)) || '—';
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {Record<string, unknown> | null}
 */
export function pickNestedUser(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return null;
  return orderLike.user ?? orderLike.appUser ?? orderLike.customer ?? orderLike.member ?? null;
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickCustomerName(orderLike) {
  const u = pickNestedUser(orderLike);
  const first = orderLike.customerFirstName ?? orderLike.firstName ?? u?.firstName;
  const last = orderLike.customerLastName ?? orderLike.lastName ?? u?.lastName;
  const combined = [first, last].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  return (
    orderLike.customerName ??
    orderLike.customerFullName ??
    u?.name ??
    u?.displayName ??
    u?.fullName ??
    ''
  );
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickCustomerEmail(orderLike) {
  const u = pickNestedUser(orderLike);
  return orderLike.customerEmail ?? orderLike.email ?? u?.email ?? u?.emailAddress ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickCustomerPhone(orderLike) {
  const u = pickNestedUser(orderLike);
  return (
    orderLike.customerPhone ??
    orderLike.phone ??
    orderLike.phoneNumber ??
    u?.phone ??
    u?.phoneNumber ??
    u?.mobile ??
    ''
  );
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickAppUserId(orderLike) {
  const u = pickNestedUser(orderLike);
  return orderLike.appUserId ?? orderLike.userId ?? u?.userId ?? u?.id ?? u?._id ?? '';
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {unknown[]}
 */
export function pickLineItems(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return [];
  const raw =
    orderLike.lineItems ??
    orderLike.items ??
    orderLike.orderLines ??
    orderLike.lines ??
    orderLike.cartItems ??
    [];
  return Array.isArray(raw) ? raw : [];
}

function normalizeAddonEntry(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s ? { name: s, price: null, quantity: 1, lineTotal: null } : null;
  }
  if (typeof raw !== 'object') return null;
  const name =
    raw.name ??
    raw.label ??
    raw.title ??
    raw.optionName ??
    raw.modifierName ??
    raw.addonName ??
    raw.description ??
    '';
  const priceRaw = raw.price ?? raw.unitPrice ?? raw.amount ?? raw.addonPrice;
  const price = priceRaw == null || priceRaw === '' ? null : Number(priceRaw);
  const quantityRaw = raw.quantity ?? raw.qty ?? raw.count ?? 1;
  const quantity = Number(quantityRaw);
  const q = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  const lineTotalRaw = raw.lineTotal ?? raw.subtotal ?? raw.total;
  const lineTotal = lineTotalRaw == null || lineTotalRaw === '' ? null : Number(lineTotalRaw);
  if (!String(name).trim() && price == null && lineTotal == null) return null;
  return {
    name: String(name).trim() || 'Add-on',
    price: price != null && Number.isFinite(price) ? price : null,
    quantity: q,
    lineTotal: lineTotal != null && Number.isFinite(lineTotal) ? lineTotal : null,
  };
}

/**
 * Add-ons / modifiers charged on top of the base dish (extra cheese, dip, etc.).
 * Accepts several API shapes on the line item.
 */
export function pickLineItemAddons(lineItem) {
  if (!lineItem || typeof lineItem !== 'object') return [];
  const out = [];
  const arrays = [
    lineItem.selectedOptions,
    lineItem.addons,
    lineItem.addOns,
    lineItem.add_ons,
    lineItem.selectedAddons,
    lineItem.modifierSelections,
    lineItem.modifiers,
    lineItem.extras,
    lineItem.customizations,
  ];
  for (const arr of arrays) {
    if (!Array.isArray(arr)) continue;
    for (const a of arr) {
      const n = normalizeAddonEntry(a);
      if (n) out.push(n);
    }
  }
  const map = lineItem.addonMap ?? lineItem.addonPrices;
  if (map && typeof map === 'object' && !Array.isArray(map)) {
    for (const [k, v] of Object.entries(map)) {
      const price = typeof v === 'number' ? v : Number(v);
      if (k && Number.isFinite(price)) out.push({ name: k, price, quantity: 1, lineTotal: null });
    }
  }
  return out;
}

/**
 * Optional bag fee / cutlery / order-level extras not tied to one dish.
 */
export function pickOrderLevelAddons(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return [];
  const out = [];
  const arrays = [orderLike.orderAddons, orderLike.globalAddons, orderLike.orderAddOns, orderLike.orderLevelAddons];
  for (const arr of arrays) {
    if (!Array.isArray(arr)) continue;
    for (const a of arr) {
      const n = normalizeAddonEntry(a);
      if (n) out.push(n);
    }
  }
  return out;
}

/** Charge for one add-on row for the parent line qty (each × add-on qty × line item qty). */
export function addonLineTotal(addon, parentLineQty) {
  if (!addon || typeof addon !== 'object') return 0;
  const pq = Number(parentLineQty);
  const parent = Number.isFinite(pq) && pq > 0 ? pq : 1;
  if (addon.lineTotal != null) {
    const t = Number(addon.lineTotal);
    if (Number.isFinite(t)) return t;
  }
  const unit = addon.price != null ? Number(addon.price) : 0;
  if (!Number.isFinite(unit)) return 0;
  const aq = addon.quantity != null ? Number(addon.quantity) : 1;
  const q = Number.isFinite(aq) && aq > 0 ? aq : 1;
  return unit * q * parent;
}

/**
 * @param {Record<string, unknown> | null | undefined} lineItem
 * @returns {string}
 */
export function lineItemLabel(lineItem) {
  if (!lineItem || typeof lineItem !== 'object') return 'Item';
  return (
    lineItem.name ??
    lineItem.title ??
    lineItem.menuItemName ??
    lineItem.itemName ??
    lineItem.productName ??
    'Item'
  );
}

/**
 * @param {Record<string, unknown> | null | undefined} lineItem
 * @returns {number}
 */
export function lineItemQty(lineItem) {
  const q = lineItem?.quantity ?? lineItem?.qty ?? lineItem?.count ?? 1;
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/** One line for tables: "2× Margherita with extra cheese, garlic dip". */
export function formatLineItemSummary(lineItem) {
  const qty = lineItemQty(lineItem);
  const label = lineItemLabel(lineItem);
  const addonNames = pickLineItemAddons(lineItem)
    .map((a) => a.name)
    .filter(Boolean);
  const base = `${qty}× ${label}`;
  if (addonNames.length === 0) return base;
  return `${base} with ${addonNames.join(', ')}`;
}

/**
 * Human-readable order contents for list tables (replaces raw order UUID subtitle).
 */
export function formatOrderTitleSummary(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return '';
  const preset =
    orderLike.orderTitle ??
    orderLike.titleSummary ??
    orderLike.itemsSummary ??
    orderLike.summary;
  if (typeof preset === 'string' && preset.trim()) return preset.trim();

  const parts = pickLineItems(orderLike).map(formatLineItemSummary);
  for (const ad of pickOrderLevelAddons(orderLike)) {
    if (ad?.name) parts.push(`+ ${ad.name}`);
  }
  return parts.join(', ');
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {number | null}
 */
export function lineItemCount(orderLike) {
  const n = pickLineItems(orderLike).length;
  return n > 0 ? n : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {number | null}
 */
export function pickTotalAmount(orderLike) {
  const v =
    orderLike?.totalAmount ??
    orderLike?.grandTotal ??
    orderLike?.total ??
    orderLike?.amount ??
    orderLike?.orderTotal;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {number | null}
 */
export function pickDeliveryFee(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return null;
  const v =
    orderLike.deliveryFee ??
    orderLike.deliveryCharge ??
    orderLike.shippingFee ??
    orderLike.shippingCharge;
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickFulfillmentLabel(orderLike) {
  const raw =
    orderLike?.fulfillmentType ??
    orderLike?.fulfillment ??
    orderLike?.orderType ??
    orderLike?.type ??
    orderLike?.deliveryMode ??
    '';
  const s = String(raw).toLowerCase();
  if (!s) return '';
  if (/(pick\s*up|pickup|collect|self|carryout|takeaway)/i.test(s)) return 'Pickup';
  if (/(delivery|dispatch|ship|drop)/i.test(s)) return 'Delivery';
  if (s === 'd') return 'Delivery';
  if (s === 'p') return 'Pickup';
  return raw ? String(raw) : '';
}

/**
 * Returns true if the order is for store pickup / takeaway.
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {boolean}
 */
export function isPickupOrder(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return false;
  const label = pickFulfillmentLabel(orderLike);
  return String(label).toLowerCase() === 'pickup';
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {{ label: string, lines: string[], name: string, contactNumber: string } | null}
 */
export function pickDeliveryAddress(orderLike) {
  const a =
    orderLike?.deliveryAddress ??
    orderLike?.shippingAddress ??
    orderLike?.address ??
    orderLike?.dropoffAddress ??
    null;
  if (!a || typeof a !== 'object') {
    const line = orderLike?.deliveryAddressLine ?? orderLike?.addressLine ?? orderLike?.fullAddress;
    const name = String(orderLike?.deliveryContactName ?? orderLike?.recipientName ?? '').trim();
    const contactNumber = String(orderLike?.deliveryContactPhone ?? orderLike?.deliveryContactNumber ?? '').trim();
    if (typeof line === 'string' && line.trim()) {
      return {
        label: String(orderLike?.addressLabel ?? '').trim(),
        lines: [line.trim()],
        name,
        contactNumber,
      };
    }
    if (name || contactNumber) {
      return { label: '', lines: [], name, contactNumber };
    }
    return null;
  }
  const label = String(a.label ?? a.title ?? orderLike?.addressLabel ?? '').trim();
  const name = String(
    a.name ??
    a.recipientName ??
    a.contactName ??
    orderLike?.deliveryContactName ??
    ''
  ).trim();
  const contactNumber = String(
    a.contactNumber ??
    a.phone ??
    a.mobile ??
    a.phoneNumber ??
    orderLike?.deliveryContactPhone ??
    orderLike?.deliveryContactNumber ??
    ''
  ).trim();

  const lines = [];
  const push = (x) => {
    if (x == null || x === '') return;
    const s = String(x).trim();
    if (s) lines.push(s);
  };
  push(a.line1 ?? a.street1 ?? a.addressLine1);
  push(a.line2 ?? a.street2 ?? a.addressLine2);
  const cityState = [a.city, a.state ?? a.region].filter(Boolean).join(', ');
  push(cityState);
  push(a.postalCode ?? a.zip ?? a.pincode ?? a.zipCode);
  push(a.country);
  const flat = lines.filter(Boolean);
  return flat.length || name || contactNumber || label
    ? { label, lines: flat, name, contactNumber }
    : null;
}

/** GeoJSON [longitude, latitude] from delivery address snapshot. */
export function pickDeliveryCoordinates(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return null;
  const a =
    orderLike.deliveryAddress ??
    orderLike.shippingAddress ??
    orderLike.address ??
    orderLike.dropoffAddress ??
    null;
  const loc = a && typeof a === 'object' ? a.location : null;
  const coords = Array.isArray(loc?.coordinates) ? loc.coordinates : null;
  if (coords && coords.length >= 2) {
    const lng = Number(coords[0]);
    const lat = Number(coords[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }
  const lat = Number(orderLike.deliveryLat ?? orderLike.latitude ?? a?.lat);
  const lng = Number(orderLike.deliveryLng ?? orderLike.longitude ?? a?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return null;
}

/**
 * Name of the recipient on the delivery address, if available.
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickDeliveryRecipientName(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return '';
  const a =
    orderLike.deliveryAddress ??
    orderLike.shippingAddress ??
    orderLike.address ??
    orderLike.dropoffAddress;
  if (a && typeof a === 'object') {
    const n = a.name ?? a.recipientName ?? a.contactName ?? a.fullName;
    if (n && String(n).trim()) return String(n).trim();
  }
  if (orderLike.deliveryContactName && String(orderLike.deliveryContactName).trim()) {
    return String(orderLike.deliveryContactName).trim();
  }
  return '';
}

/**
 * Contact phone on the delivery address, falling back to account customer phone.
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickDeliveryContactPhone(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') return '';
  const a =
    orderLike.deliveryAddress ??
    orderLike.shippingAddress ??
    orderLike.address ??
    orderLike.dropoffAddress;
  if (a && typeof a === 'object') {
    const n = a.contactNumber ?? a.phone ?? a.mobile ?? a.phoneNumber;
    if (n && String(n).trim()) return String(n).trim();
  }
  if (orderLike.deliveryContactPhone && String(orderLike.deliveryContactPhone).trim()) {
    return String(orderLike.deliveryContactPhone).trim();
  }
  return pickCustomerPhone(orderLike);
}

/**
 * @param {unknown} raw
 * @returns {string}
 */
export function humanizePaymentMethod(raw) {
  const m = String(raw ?? '')
    .trim()
    .toUpperCase();
  if (!m) return '';
  if (m === 'COD') return 'Cash on delivery';
  if (m === 'ONLINE') return 'Online (Razorpay)';
  return String(raw);
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {{ method: string, status: string, id: string }}
 */
/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {boolean}
 */
export function isCodOrder(orderLike) {
  const methodRaw = orderLike?.paymentMethod ?? orderLike?.paymentType ?? orderLike?.payMode ?? '';
  return String(methodRaw).trim().toUpperCase() === 'COD';
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {{ method: string, status: string, id: string }}
 */
export function pickPaymentSummary(orderLike) {
  const methodRaw = orderLike?.paymentMethod ?? orderLike?.paymentType ?? orderLike?.payMode ?? '';
  const method = humanizePaymentMethod(methodRaw);
  const status = orderLike?.paymentStatus ?? orderLike?.paymentState ?? '';
  const id = orderLike?.paymentIntentId ?? orderLike?.transactionId ?? orderLike?.paymentId ?? '';
  return { method, status: status ? String(status) : '', id: id ? String(id) : '' };
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {{ invoiceNumber: string, invoiceUrl: string }}
 */
export function pickInvoiceFields(orderLike) {
  if (!orderLike || typeof orderLike !== 'object') {
    return { invoiceNumber: '', invoiceUrl: '' };
  }
  return {
    invoiceNumber: orderLike.invoiceNumber ? String(orderLike.invoiceNumber) : '',
    invoiceUrl: orderLike.invoiceUrl ? String(orderLike.invoiceUrl) : '',
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} orderLike
 * @returns {string}
 */
export function pickEstimatedDeliveryText(orderLike) {
  const min = orderLike?.estimatedDeliveryMinutesMin ?? orderLike?.etaMinutesMin;
  const max = orderLike?.estimatedDeliveryMinutesMax ?? orderLike?.etaMinutesMax;
  if (min != null && max != null && Number(min) >= 0 && Number(max) >= Number(min)) {
    return `${min}-${max} min`;
  }
  const single = orderLike?.estimatedDeliveryMinutes ?? orderLike?.etaMinutes;
  if (single != null && Number(single) >= 0) return `~${single} min`;
  const until = orderLike?.estimatedDeliveryAt ?? orderLike?.estimatedReadyAt ?? orderLike?.promisedAt;
  if (until) {
    try {
      return new Date(until).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return String(until);
    }
  }
  return '';
}

function normalizeHistoryEntry(h) {
  if (!h || typeof h !== 'object') return null;
  const status = h.status ?? h.to ?? h.newStatus ?? h.state ?? h.value;
  const at = h.at ?? h.createdAt ?? h.timestamp ?? h.time ?? h.updatedAt ?? h.occurredAt;
  if (!status || !at) return null;
  return { status: String(status).toUpperCase(), at: String(at) };
}

/** Latest timestamp per status from API history + common flat fields. */
export function collectStatusTimestamps(order) {
  const map = new Map();
  const put = (status, t) => {
    if (!status || !t) return;
    const st = String(status).toUpperCase();
    const prev = map.get(st);
    if (!prev) {
      map.set(st, t);
      return;
    }
    try {
      if (new Date(t) >= new Date(prev)) map.set(st, t);
    } catch {
      map.set(st, t);
    }
  };

  const buckets = [
    order?.statusHistory,
    order?.statusUpdates,
    order?.statusChanges,
    order?.timeline,
    order?.tracking,
    order?.events,
  ];
  for (const arr of buckets) {
    if (!Array.isArray(arr)) continue;
    for (const h of arr) {
      const n = normalizeHistoryEntry(h);
      if (n) put(n.status, n.at);
      if (h && typeof h === 'object' && h.message && h.createdAt) {
        put(order?.status, h.createdAt);
      }
    }
  }

  const flat = [
    ['PENDING', order?.pendingAt],
    ['CONFIRMED', order?.confirmedAt ?? order?.orderConfirmedAt],
    ['PREPARING', order?.preparingAt ?? order?.kitchenStartedAt],
    ['READY_FOR_PICKUP', order?.readyAt ?? order?.readyForPickupAt],
    ['ON_THE_WAY', order?.outForDeliveryAt ?? order?.dispatchedAt],
    ['DELIVERED', order?.deliveredAt],
    ['CANCELLED', order?.cancelledAt],
    ['REFUNDED', order?.refundedAt],
  ];
  for (const [st, t] of flat) put(st, t);

  const cur = order?.status;
  if (cur && !map.has(String(cur).toUpperCase())) {
    put(cur, order?.statusChangedAt ?? order?.updatedAt ?? order?.modifiedAt ?? order?.placedAt ?? order?.createdAt);
  }

  return map;
}

/**
 * Sort key for vertical tracker (PENDING=-1, terminal/refunded at end).
 * @param {string | undefined} status
 * @returns {number}
 */
export function trackingRank(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PENDING') return -1;
  if (s === 'CANCELLED') return -2;
  if (s === 'REFUNDED') return ORDER_TRACKING_FLOW.length - 1;
  const i = ORDER_TRACKING_FLOW.indexOf(s);
  return i >= 0 ? i : -1;
}

/**
 * @param {string | undefined} status
 * @returns {boolean}
 */
export function isTerminalStatus(status) {
  const s = String(status || '').toUpperCase();
  return s === 'REFUNDED' || s === 'CANCELLED';
}

/**
 * Allowed next statuses for the kitchen / ops dropdown — forward chain only.
 * `CANCELLED` and `REFUNDED` are not selectable here; use the explicit
 * "Cancel order" and "Process refund" actions instead, which call the
 * dedicated backend endpoints that talk to Razorpay.
 */
export function statusSelectOptions(currentStatus, orderLikeOrIsPickup) {
  const isPickup =
    typeof orderLikeOrIsPickup === 'boolean'
      ? orderLikeOrIsPickup
      : typeof orderLikeOrIsPickup === 'string'
        ? /(pick\s*up|pickup|collect|self|carryout|takeaway)/i.test(orderLikeOrIsPickup)
        : isPickupOrder(orderLikeOrIsPickup);
  const chain = isPickup ? PICKUP_STATUS_FORWARD_CHAIN : ORDER_STATUS_FORWARD_CHAIN;
  const cur = String(currentStatus || '').toUpperCase();
  if (cur === 'REFUNDED') return ['REFUNDED'];
  if (cur === 'CANCELLED') return ['CANCELLED'];
  if (cur === 'DELIVERED') return ['DELIVERED'];
  const idx = chain.indexOf(cur);
  if (idx >= 0) {
    return chain.slice(idx);
  }
  return [...chain];
}
